import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import Swal from "sweetalert2";

import PageTitle from "../../layouts/PageTitle";
import { selectActiveProjectId } from "../../../store/projectSlice";
import { selectUserRole } from "../../../store/authSlice";
import {
  cwListProjects,
  cwGetDailyRoster,
  cwSetWorkerOut,
  cwGetProjectList,
  cwForceMark,
} from "../../../services/contractWorkers";

// ─────────────────────────────────────────────────────────────────────────────
// Contract Workers → Daily Roster
// One project + date: the attendance photo card(s) captured that day (one card
// per save — two saves show two images) and the list of every worker marked,
// with Full / Half / Leave.
// ─────────────────────────────────────────────────────────────────────────────

const API_ORIGIN = process.env.REACT_APP_API_URL || "http://localhost:5001";
const resolveUrl = (u) =>
  !u ? null : u.startsWith("http") ? u : `${API_ORIGIN}${u}`;
// Local calendar date (YYYY-MM-DD), NOT the UTC date — toISOString() slices
// to UTC, which rolls back to "yesterday" for any IST time before 5:30am.
// Must match the same helper in ContractWorkerAttendance.jsx so "today" means
// the same day on both the marking screen and this roster.
const todayStr = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const STATUS_BADGE = {
  full: "success",
  half: "warning",
  leave: "info",
};

// Super-admin override: set any worker's attendance for the chosen date —
// no photo needed, and it replaces whatever is already recorded.
const ForceMarkPanel = ({ projectId, date, onDone }) => {
  const [list, setList] = useState([]);
  const [marked, setMarked] = useState({});
  const [pick, setPick] = useState({});
  const [savedRemarks, setSavedRemarks] = useState({});
  const [remarkDraft, setRemarkDraft] = useState({});
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    cwGetProjectList(projectId, date)
      .then((r) => {
        setList(r.data?.workers || []);
        setMarked(r.data?.markedStatuses || {});
        setSavedRemarks(r.data?.markedRemarks || {});
        setPick({});
        setRemarkDraft({});
      })
      .catch(() => setList([]));
  }, [open, projectId, date]);

  // A row counts as changed when its status changes or its remark is edited
  // (a remark alone keeps the current status, or full day if not marked).
  const remarkChanged = (id) =>
    remarkDraft[id] !== undefined && remarkDraft[id].trim() !== (savedRemarks[id] || "");
  const changed = [
    ...new Set([...Object.keys(pick), ...Object.keys(remarkDraft)]),
  ]
    .map((id) => {
      const st = pick[id];
      const statusChanged = st && st !== (marked[id] || (st === "absent" ? "absent" : ""));
      if (!statusChanged && !remarkChanged(id)) return null;
      const status = statusChanged ? st : marked[id] || "full";
      return [id, status];
    })
    .filter(Boolean);

  const apply = async () => {
    setBusy(true);
    try {
      await cwForceMark(
        projectId,
        date,
        changed.map(([workerId, status]) => ({
          workerId,
          status,
          ...(remarkDraft[workerId] !== undefined
            ? { remarks: remarkDraft[workerId].trim() }
            : {}),
        })),
      );
      Swal.fire({ icon: "success", title: `${changed.length} updated`, timer: 1200, showConfirmButton: false });
      setOpen(false);
      onDone();
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err?.response?.data?.message || "Could not update.",
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border rounded p-3 mb-3 bg-light">
      <div className="d-flex align-items-center gap-2">
        <strong className="me-auto">Super Admin — Force attendance for {date}</strong>
        <button className="btn btn-sm btn-outline-primary" onClick={() => setOpen((v) => !v)}>
          {open ? "Close" : "Open"}
        </button>
      </div>
      {open && (
        <>
          <div className="text-muted small my-2">
            Set Full / Half / Leave for any worker on this date, without a photo — or
            Absent to remove a present mark (e.g. punched then left early).
            It replaces an existing entry.
          </div>
          <div className="table-responsive" style={{ maxHeight: 320 }}>
            <table className="table table-sm align-middle mb-2">
              <thead>
                <tr>
                  <th>Worker</th>
                  <th>Current</th>
                  <th style={{ width: 160 }}>Set to</th>
                  <th style={{ minWidth: 180 }}>Remarks</th>
                </tr>
              </thead>
              <tbody>
                {list.map((w) => (
                  <tr key={w._id}>
                    <td className="text-capitalize">{w.name}</td>
                    <td>{marked[w._id] || <span className="text-muted">not marked</span>}</td>
                    <td>
                      <select
                        className="form-select form-select-sm"
                        value={pick[w._id] || ""}
                        onChange={(e) => setPick((p) => ({ ...p, [w._id]: e.target.value }))}
                      >
                        <option value="">— no change —</option>
                        <option value="full">Full day</option>
                        <option value="half">Half day</option>
                        <option value="leave">Leave</option>
                        <option value="absent">Absent (punched, left early)</option>
                      </select>
                    </td>
                    <td>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="Optional remark"
                        maxLength={300}
                        value={
                          remarkDraft[w._id] !== undefined
                            ? remarkDraft[w._id]
                            : savedRemarks[w._id] || ""
                        }
                        onChange={(e) =>
                          setRemarkDraft((p) => ({ ...p, [w._id]: e.target.value }))
                        }
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button className="btn btn-sm btn-primary" disabled={busy || !changed.length} onClick={apply}>
            {busy ? "Saving…" : `Apply to ${changed.length} worker${changed.length === 1 ? "" : "s"}`}
          </button>
        </>
      )}
    </div>
  );
};

const ContractWorkerDailyRoster = () => {
  const activeProjectId = useSelector(selectActiveProjectId);
  const isSuperAdmin = useSelector(selectUserRole) === "super-admin";

  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState(activeProjectId || "");
  const [date, setDate] = useState(todayStr());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [zoom, setZoom] = useState(null);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    cwListProjects()
      .then((r) => setProjects(r.data?.projects || []))
      .catch(() => setProjects([]));
  }, []);

  const load = useCallback(() => {
    if (!projectId) {
      setData(null);
      return;
    }
    setLoading(true);
    cwGetDailyRoster(projectId, date)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [projectId, date]);

  useEffect(() => {
    load();
  }, [load]);

  const setOut = async (worker, status) => {
    setBusyId(worker.workerId);
    try {
      await cwSetWorkerOut(projectId, worker.workerId, date, status);
      load();
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err?.response?.data?.message || "Could not update.",
      });
    } finally {
      setBusyId(null);
    }
  };

  const photos = data?.photos || [];
  const workers = data?.workers || [];
  const s = data?.summary || { full: 0, half: 0, leave: 0 };

  return (
    <>
      <PageTitle activeMenu="Daily Roster" motherMenu="Contract Workers" />
      <div className="card">
        <div className="card-header d-flex flex-wrap gap-2 align-items-center">
          <h4 className="card-title mb-0 me-auto">Daily Roster</h4>
          <select
            className="form-select form-select-sm"
            style={{ width: 210 }}
            value={projectId}
            onChange={(e) => setProjectId(e.target.value)}
          >
            <option value="">Select project…</option>
            {projects.map((p) => (
              <option key={p._id} value={p._id}>
                {p.projectName}
              </option>
            ))}
          </select>
          <input
            type="date"
            max={todayStr()}
            className="form-control form-control-sm"
            style={{ width: 160 }}
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
        </div>
        <div className="card-body">
          {loading ? (
            <div className="text-center py-4">Loading…</div>
          ) : !projectId ? (
            <div className="text-muted text-center py-4">
              Select a project.
            </div>
          ) : (
            <>
              {isSuperAdmin && (
                <ForceMarkPanel projectId={projectId} date={date} onDone={load} />
              )}

              {/* Attendance photo card(s) — one per save */}
              <div className="d-flex flex-wrap gap-3 mb-3">
                {photos.length === 0 ? (
                  <div className="text-muted">
                    No attendance recorded on {date}.
                  </div>
                ) : (
                  photos.map((p, i) => (
                    <div
                      key={i}
                      className="border rounded"
                      style={{ width: 220, overflow: "hidden" }}
                    >
                      <img
                        src={resolveUrl(p.url)}
                        alt={`Attendance ${i + 1}`}
                        style={{
                          width: "100%",
                          height: 160,
                          objectFit: "cover",
                          cursor: "pointer",
                        }}
                        onClick={() => setZoom(resolveUrl(p.url))}
                      />
                      <div className="p-2 small text-muted">
                        {p.mode === "individual" ? "Individual" : "Group"} ·{" "}
                        {p.workerCount} worker
                        {p.workerCount === 1 ? "" : "s"}
                        {p.markedBy ? (
                          <span className="d-block">by {p.markedBy}</span>
                        ) : null}
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Worker list */}
              {workers.length > 0 && (
                <div className="mb-2 d-flex gap-3 small">
                  <span>
                    <strong>{s.full}</strong> full
                  </span>
                  <span>
                    <strong>{s.half}</strong> half
                  </span>
                  <span>
                    <strong>{s.leave}</strong> leave
                  </span>
                </div>
              )}
              <div className="table-responsive">
                <table className="table table-hover align-middle">
                  <thead>
                    <tr>
                      <th>Worker</th>
                      <th>Profile</th>
                      <th>Shift</th>
                      <th>Time</th>
                      <th>Attendance</th>
                      <th>Remarks</th>
                      <th aria-label="Action" />
                    </tr>
                  </thead>
                  <tbody>
                    {workers.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="text-center text-muted py-3">
                          No workers marked on this date.
                        </td>
                      </tr>
                    ) : (
                      workers.map((w) => (
                        <tr key={w.workerId}>
                          <td className="fw-semibold text-capitalize">
                            {w.name}
                            {w.contractCompany ? (
                              <small className="d-block text-muted">
                                {w.contractCompany}
                              </small>
                            ) : null}
                          </td>
                          <td>{w.workProfile}</td>
                          <td>{w.shift || "Morning"}</td>
                          <td
                            className="text-nowrap"
                            style={{ fontVariantNumeric: "tabular-nums" }}
                          >
                            {w.markedAt
                              ? new Date(w.markedAt).toLocaleTimeString([], {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })
                              : "—"}
                            {w.markedBy ? (
                              <small className="d-block text-muted">
                                by {w.markedBy}
                              </small>
                            ) : null}
                          </td>
                          <td>
                            <span
                              className={`badge bg-${STATUS_BADGE[w.status] || "secondary"}`}
                            >
                              {w.status === "full"
                                ? "Full day"
                                : w.status === "half"
                                  ? "Half day"
                                  : "Leave"}
                            </span>
                          </td>
                          <td className="small text-muted" style={{ maxWidth: 260 }}>
                            {w.remarks || "—"}
                          </td>
                          <td className="text-end">
                            {w.status === "full" && (
                              <button
                                className="btn btn-xs btn-outline-warning"
                                disabled={busyId === w.workerId}
                                onClick={() => setOut(w, "half")}
                              >
                                Out (½ day)
                              </button>
                            )}
                            {w.status === "half" && (
                              <button
                                className="btn btn-xs btn-outline-secondary"
                                disabled={busyId === w.workerId}
                                onClick={() => setOut(w, "full")}
                              >
                                Undo — full day
                              </button>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>

      {zoom && (
        <div
          onClick={() => setZoom(null)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.8)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            cursor: "zoom-out",
          }}
        >
          <img
            src={zoom}
            alt="attendance"
            style={{ maxHeight: "85vh", maxWidth: "90vw", borderRadius: 8 }}
          />
        </div>
      )}
    </>
  );
};

export default ContractWorkerDailyRoster;
